import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { DIVISIONS, AWARDS, ALL_LEVELS } from '../data/standoutsConstants';
import StandoutAthleteCard from '../components/StandoutAthleteCard';
import '../styles/WeeklyStandouts.css';

const formatName = (fullName) => {
  if (!fullName) return '';
  const parts = fullName.trim().split(' ');
  if (parts.length < 2) return fullName;
  return `${parts[0]} ${parts[parts.length - 1].charAt(0)}.`;
};

const ChevronDown = () => (
  <img src="/assets/images/standout-arrow-down.png" alt="" style={{ width: '11px', height: 'auto', objectFit: 'contain' }} />
);

const PinIcon = () => (
  <img src="/assets/images/standout-location.png" alt="" style={{ width: '9px', height: 'auto', objectFit: 'contain' }} />
);

const SportIcon = () => (
  <img src="/assets/images/standout-soccer.png" alt="" style={{ width: '15px', height: 'auto', objectFit: 'contain' }} />
);

const WeeklyStandouts = () => {
  const { weekId } = useParams();
  const navigate = useNavigate();
  const { getApprovedStandouts } = useAuth();

  const [allStandouts, setAllStandouts] = useState([]);
  const [loading, setLoading] = useState(true);

  const [selectedLocation, setSelectedLocation] = useState('all');
  const [selectedSport, setSelectedSport] = useState('all');
  const [selectedDivision, setSelectedDivision] = useState('all');
  const [selectedLevel, setSelectedLevel] = useState('all');
  const [selectedAward, setSelectedAward] = useState('all');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const result = await getApprovedStandouts();
      if (cancelled) return;
      if (result.success) setAllStandouts(result.data || []);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [getApprovedStandouts]);

  // Treat weekId from URL as URL-encoded week_label, e.g. "Week-3"
  const requestedWeekLabel = weekId ? decodeURIComponent(weekId).replace(/-/g, ' ') : null;

  // Build unique weeks from data
  const weeksMap = new Map();
  allStandouts.forEach((s) => {
    if (s.week_label && !weeksMap.has(s.week_label)) {
      weeksMap.set(s.week_label, {
        id: s.week_label.toLowerCase().replace(/\s+/g, '-'),
        label: s.week_label,
        dateRange: s.week_date_range || '',
        season: s.season || '',
      });
    }
  });
  const weeks = Array.from(weeksMap.values());

  // Find the requested week or fall back to most recent
  const week = weeks.find((w) =>
    w.label === requestedWeekLabel || w.id === weekId
  ) || weeks[weeks.length - 1] || { label: requestedWeekLabel || '', dateRange: '', season: '' };

  const weekIndex = weeks.findIndex((w) => w.label === week.label);
  const prevWeek = weekIndex > 0 ? weeks[weekIndex - 1] : null;
  const nextWeek = weekIndex >= 0 && weekIndex < weeks.length - 1 ? weeks[weekIndex + 1] : null;

  const weekStandouts = allStandouts.filter((s) => s.week_label === week.label);

  const normalizeLocation = (l) => {
    const parts = l.split(',');
    if (parts.length >= 2) return `${parts[0].trim()}, ${parts[1].trim().toUpperCase()}`;
    return l.trim();
  };

  const allLocations = [
    ...new Map(
      weekStandouts
        .map((s) => s.org_location)
        .filter(Boolean)
        .map((l) => [l.toUpperCase(), normalizeLocation(l)])
    ).values(),
  ];
  const allSports = [...new Set(
    weekStandouts
      .filter((s) => selectedLocation === 'all' || s.org_location?.toUpperCase() === selectedLocation?.toUpperCase())
      .map((s) => s.sport)
      .filter(Boolean)
  )];

  // Pick a representative org for the title header
  const titleOrg = (() => {
    const match = weekStandouts.find(
      (s) =>
        (selectedLocation === 'all' || s.org_location?.toUpperCase() === selectedLocation?.toUpperCase()) &&
        (selectedSport === 'all' || s.sport === selectedSport)
    );
    return match || weekStandouts[0] || null;
  })();

  const filtered = weekStandouts.filter((s) => {
    return (
      (selectedLocation === 'all' || s.org_location?.toUpperCase() === selectedLocation?.toUpperCase()) &&
      (selectedSport === 'all' || s.sport === selectedSport) &&
      (selectedDivision === 'all' || s.division === selectedDivision) &&
      (selectedLevel === 'all' || s.level === selectedLevel) &&
      (selectedAward === 'all' || s.award === selectedAward)
    );
  });

  const pageTitle = titleOrg
    ? `${titleOrg.org_name} – ${week.label} Standouts`.toUpperCase()
    : `${week.label} Standouts`.toUpperCase();

  const pageSubtitleLeft = titleOrg ? titleOrg.org_location : null;
  const pageSubtitleRight = week.dateRange;

  const locationLabel = selectedLocation === 'all'
    ? (allLocations[0] || 'All Locations')
    : selectedLocation;

  const sportLabel = selectedSport === 'all'
    ? (allSports[0] || 'All Sports')
    : selectedSport;

  return (
    <div className="ws2-page">
      <div className="ws2-inner">
      {/* ── Top Bar ── */}
      <div className="ws2-topbar">
        <div className="ws2-logo-group">
          <img src="/assets/images/standoutlogo.png" alt="LegacyStat" className="ws2-logo" />
        </div>
        <button className="ws2-view-all-btn" onClick={() => navigate('/standouts')}>
          View All Standouts
        </button>
      </div>

      <div className="ws2-heading-divider" />

      {/* ── Title Block ── */}
      <div className="ws2-title-block">
        <h1 className="ws2-main-title">{pageTitle}</h1>
        <p className="ws2-subtitle">
          {pageSubtitleLeft && (
            <>
              {pageSubtitleLeft}
              <img src="/assets/images/standout-line.png" alt="" className="ws2-subtitle-pipe" />
            </>
          )}
          {pageSubtitleRight}
        </p>
        <div className="ws2-coaches-row">
          <span className="ws2-coaches-dash" />
          <span className="ws2-coaches-text">Selected by Coaches</span>
          <span className="ws2-coaches-dash" />
        </div>
      </div>

      <div className="ws2-heading-divider" />

      {/* ── Filter Row 1 ── */}
      <div className="ws2-filters-r1">
        <button
          className={`ws2-pill ${selectedLocation !== 'all' ? 'active' : ''}`}
          onClick={() => setSelectedLocation(selectedLocation === 'all' ? (allLocations[0] || 'all') : 'all')}
        >
          <PinIcon />
          <span>{locationLabel}</span>
          <ChevronDown />
        </button>

        <button
          className={`ws2-pill ${selectedSport !== 'all' ? 'active' : ''}`}
          onClick={() => setSelectedSport(selectedSport === 'all' ? (allSports[0] || 'all') : 'all')}
        >
          <SportIcon />
          <span>{sportLabel}</span>
          <ChevronDown />
        </button>

        <div className="ws2-week-pill">
          <span className="ws2-week-label-text">{week.label}</span>
          <img src="/assets/images/standout-line.png" alt="" className="ws2-week-pipe-img" />
          <span className="ws2-week-dates">{week.dateRange}</span>
          {nextWeek && (
            <button
              className="ws2-week-arrow"
              onClick={() => navigate(`/standouts/week/${nextWeek.id}`)}
            >
              <img src="/assets/images/standout-right.png" alt="next" style={{ width: '10px', height: 'auto' }} />
            </button>
          )}
        </div>
      </div>

      {/* ── Filter Row 2 ── */}
      <div className="ws2-filters-r2">
        <div className="ws2-sub-select-wrap">
          <select
            className={`ws2-sub-select ${selectedDivision !== 'all' ? 'active' : ''}`}
            value={selectedDivision}
            onChange={(e) => setSelectedDivision(e.target.value)}
          >
            <option value="all">Division: All</option>
            {DIVISIONS.map((d) => (
              <option key={d} value={d}>Division: {d}</option>
            ))}
          </select>
          <ChevronDown />
        </div>

        <div className="ws2-sub-select-wrap">
          <select
            className={`ws2-sub-select ${selectedLevel !== 'all' ? 'active' : ''}`}
            value={selectedLevel}
            onChange={(e) => setSelectedLevel(e.target.value)}
          >
            <option value="all">Level: All</option>
            {ALL_LEVELS.map((l) => (
              <option key={l} value={l}>Level: {l}</option>
            ))}
          </select>
          <ChevronDown />
        </div>

        <div className="ws2-sub-select-wrap">
          <select
            className={`ws2-sub-select ${selectedAward !== 'all' ? 'active' : ''}`}
            value={selectedAward}
            onChange={(e) => setSelectedAward(e.target.value)}
          >
            <option value="all">Recognition: All Types</option>
            {AWARDS.map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
          <ChevronDown />
        </div>
      </div>

      {/* ── Cards Grid ── */}
      {loading ? (
        <div className="ws2-empty">Loading...</div>
      ) : filtered.length === 0 ? (
        <div className="ws2-empty">No standouts match your filters for this week.</div>
      ) : (
        <div className="ws2-grid">
          {filtered.map((s) => (
            <StandoutAthleteCard
              key={s.id}
              image={s.photo_url}
              name={formatName(s.athlete_name)}
              sport={s.org_name}
              timeLabel={s.team_name}
              award={s.award}
              onBadgeClick={() => navigate(`/standouts/athlete/${s.id}`)}
            />
          ))}
        </div>
      )}

      {/* ── Bottom Week Nav ── */}
      <div className="ws2-bottom-nav">
        <button
          className="ws2-nav-btn"
          onClick={() => prevWeek && navigate(`/standouts/week/${prevWeek.id}`)}
          disabled={!prevWeek}
        >
          <img src="/assets/images/standout-left.png" alt="prev" style={{ width: '12px', height: 'auto', marginRight: '6px' }} />
          Previous Week
        </button>

        <div className="ws2-nav-center">
          {week.label}
          <img src="/assets/images/standout-line.png" alt="" className="ws2-subtitle-pipe" />
          {week.dateRange}
        </div>

        <button
          className="ws2-nav-btn"
          onClick={() => nextWeek && navigate(`/standouts/week/${nextWeek.id}`)}
          disabled={!nextWeek}
        >
          Next Week
          <img src="/assets/images/standout-right.png" alt="next" style={{ width: '12px', height: 'auto', marginLeft: '6px' }} />
        </button>
      </div>

      <div className="ws2-bottom-divider" />

      <div className="ws2-view-weeks-row">
        <button className="ws2-view-weeks-btn" onClick={() => navigate('/standouts')}>
          &#9776;&nbsp; View All Weeks
        </button>
      </div>
      </div>
    </div>
  );
};

export default WeeklyStandouts;
